import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  Inject,
  Renderer2,
  HostListener,
  PLATFORM_ID,
  Optional
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { takeUntil, catchError, tap } from 'rxjs/operators';
import { AosToken, aos } from '../../shared/aos';

import { environment } from '../../../environments/environment';
import { SwiperContainerComponent } from '../../shared/swiper/swiper.component';
import { LangService } from 'src/app/services/lang.service';
import { HeadLinkService } from 'src/app/services/head-link.service';
import { CarService } from 'src/app/services/car.service';
import { SsrTransferStateService } from 'src/app/services/ssr-transfer-state.service';
import {CarDetail, getOGPImageUrl} from 'src/app/models/car';
import { Lang } from 'src/app/models/lang';
import { Strings } from '../../const/strings';
import { carDetailSliderConfig } from '../../const/car-detail-slider-config';
import { TrackingService } from 'src/app/services/tracking.service';
import { ServerError } from '../../../Exceptions/ServerError';
import { ElementRef } from '@angular/core';

declare var Swiper: any;

@Component({
  selector: 'app-car-detail',
  templateUrl: './car-detail.component.html',
  styleUrls: ['./car-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarDetailComponent implements OnInit, OnDestroy {

  car$: Observable<CarDetail>;
  destroy$: Subject<boolean> = new Subject<boolean>();

  id: string;
  appUrl: string;
  lang: Lang;
  path: string;
  strings = Strings;
  skeletonThumbnailNumbers = Array(12);
  skeletonDescNumbers = Array(12);
  headerNavVisible = false;
  sliderNextButtonDisabled = false;
  sliderPrevButtonDisabled = false;
  currentSliderIndex = 0;
  sliderProcessTransition$: Subject<boolean> = new Subject<boolean>();
  pageTitle: string;
  twitterShareUrl: string;
  fbShareUrl: string;

  @ViewChild('carSlider') carSlider: SwiperContainerComponent;
  swipeOptions = carDetailSliderConfig;

  constructor(
    @Optional() @Inject(RESPONSE) private response: Response,
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private snackBar: MatSnackBar,
    private metaService: Meta,
    private titleService: Title,
    private elementRef: ElementRef,
    private langService: LangService,
    private carService: CarService,
    private ssrTransferState: SsrTransferStateService,
    private headLinkService: HeadLinkService,
    private googleAnalyticsService: TrackingService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(AosToken) aos: any,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  ngOnInit() {
    this.appUrl = this.langService.getAppUrl();
    this.lang = this.langService.getCurrentLang();
    this.setBodyClassName();
    this.setTitle(this.strings.carDetail.title[this.lang]);
    this.route.params.subscribe(params => {
      this.id = params.id;
      this.path = `/${this.lang}/cars/${this.id}`;
      this.setHeadLink();
      this.setup();
    });
    this.setupAOS();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  @HostListener('window:scroll', [ '$event' ])
  onWindowScroll(event: Event) {
    const scrollYKey = 'scrollY';
    if (event.currentTarget[scrollYKey] > 500) {
      this.headerNavVisible = true;
    } else {
      this.headerNavVisible = false;
    }
  }

  getCarLabelClassName(car: CarDetail): string {
    if (car.sold) {
      return 'soldout';
    } else if (car.inTransaction) {
      return 'in_transaction';
    } else if (car.previousPrice) {
      return 'discount';
    } else if (car.new) {
      return 'newcar';
    }
    return '';
  }

  sendAdsTelEvent(url: string) {
    this.googleAnalyticsService.sendAdsTelEvent(url);
  }

  private setBodyClassName() {
    if (typeof document === 'undefined') {
      return;
    }
    this.renderer.addClass(document.body, 'cardetail_page_body');
    this.renderer.addClass(document.body, 'secondlevel_page_body');
    if (this.lang === 'en') {
      this.renderer.addClass(document.body, 'lang_en');
    }
  }

  private setTitle(title: string) {
    this.titleService.setTitle(title);
  }

  private setHeadLink() {
    const url = this.langService.getAppUrl() + '/cars/' + this.id;
    this.headLinkService.setCanonicalURL(url);
    this.headLinkService.setAlternate(url, this.lang);
  }

  private setup() {
    this.car$ = this.ssrTransferState.get(
      this.elementRef.nativeElement.tagName + '-CAR',
      this.carService.fetchOne(this.id, this.lang)
    )
      .pipe(
        takeUntil(this.destroy$),
        tap(car => {
          // tslint:disable-next-line: no-string-literal
          if (car['status'] !== undefined && car['status'] === 500) {
            // 500 Internal Server Error
            throw new ServerError('Internal Server Error');
          }
          this.setupCarDetail(car);
        }),
        catchError(error => {
          if (error instanceof ServerError) {
            // 500 Internal Server Error
            if (isPlatformServer(this.platformId) && this.response) {
              this.response.status(503);
            }
            if (isPlatformBrowser(this.platformId)) {
              this.snackBar.open(this.strings.ServerError.ServiceUnavailable[this.lang]);
            }
            throw new ServerError('Internal Server Error');
          }
          this.router.navigate(['/not-found']);
          throw new Error(error);
        })
      );
  }

  private setupCarDetail(car: CarDetail) {
    this.pageTitle = `${car.name} | CARZY`;
    this.setTitle(this.pageTitle);
    this.sendGoogleAnalytics();
    this.setTwitterShareUrl();
    this.setFbShareUrl();
    this.addMeta(car);
    this.setupAOS();
    this.setupSlideEvent(car.photos.length);
  }

  private sendGoogleAnalytics() {
    this.googleAnalyticsService.sendPageView(this.path, this.pageTitle);
  }

  private addMeta(car: CarDetail) {
    const modelYearSuffix = this.strings.car.modelYear[this.lang] + ' / ';
    const modelYear = (car.modelYear && car.modelYear !== 0) ? car.modelYear + modelYearSuffix : '';
    const description = this.createMetaDescription(car, modelYear);
    this.metaService.addTags([
      { name: 'description', content: description },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'thumbnail', content: car.photos[0].url },
      { property: 'og:url', content: this.pageUrl() },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: this.pageTitle },
      { property: 'og:description', content: description },
      { property: 'og:image', content: getOGPImageUrl(car) },
      { property: 'fb:app_id', content: environment.facebookAppId },
    ]);
  }

  private createMetaDescription(car: CarDetail, modelYear: string): string {
    let description = `${modelYear}${car.name} / ${car.copy.replace(/\n/, ' ')}`;
    const rangeSymbol = this.strings.carPrice.rangeSymbol[this.lang];
    if (car.price) {
      description += ` / ${this.lang === 'ja' ? '車両価格 ' : ''}${car.price}`;
    }
    if (car.area) {
      description += ` / ${car.area}`;
    }
    if (!car.sold) {
      description += ` / ${this.strings.car.inStock[this.lang]}`;
    }
    if (car.distance) {
      description += ` / ${car.distance}km`;
    }
    if (car.displacement) {
      description += ` / ${car.displacement}cc`;
    }
    if (car.color) {
      description += ` / ${this.strings.carSpec.color[this.lang]}: ${car.color}`;
    }
    return description;
  }

  private pageUrl(): string {
    return `${environment.baseUrl}/${this.lang}/cars/${this.id}`;
  }

  private setupAOS() {
    if (typeof document === 'undefined') {
      return;
    }
    aos.init({
      duration: 1200,
      delay: 200,
      once: true,
      mirror: true,
    });
  }

  private setupSlideEvent(photoCount: number) {
    if (typeof document === 'undefined') {
      return;
    }
    this.sliderPrevButtonDisabled = true;
    this.sliderNextButtonDisabled = (photoCount <= 1);
    setTimeout(() => {
      this.carSlider.swiper.on('slideChange', () => {
        this.currentSliderIndex = this.carSlider.swiper.realIndex;
        this.sliderPrevButtonDisabled = this.carSlider.swiper.isBeginning;
        this.sliderNextButtonDisabled = this.carSlider.swiper.isEnd;
      });
      this.carSlider.swiper.on('slideChangeTransitionStart', () => {
        this.sliderProcessTransition$.next(true);
      });
      this.carSlider.swiper.on('slideChangeTransitionEnd', () => {
        this.sliderProcessTransition$.next(false);
      });
    }, 800);
  }

  slideTo(index: number) {
    this.carSlider.swiper.slideTo(index);
  }

  private setTwitterShareUrl() {
    const text = `${this.pageTitle}\n${this.pageUrl()}`;
    const twitterText = encodeURIComponent(text);
    this.twitterShareUrl = `https://twitter.com/intent/tweet?text=${twitterText}`;
  }

  shareTwitter(event: Event) {
    event.preventDefault();
    this.googleAnalyticsService.sendSNS('Twitter', this.path);
    const windowName = 'Twiter_Share';
    const features = this.generateWindowFeatures();
    window.open(this.twitterShareUrl, windowName, features);
  }

  private setFbShareUrl() {
    const encodedUrl = encodeURIComponent(this.pageUrl());
    this.fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  }

  shareFB(event: Event) {
    event.preventDefault();
    this.googleAnalyticsService.sendSNS('Facebook', this.path);
    const windowName = 'FB_Share';
    const features = this.generateWindowFeatures();
    window.open(this.fbShareUrl, windowName, features);
  }

  private generateWindowFeatures(): string {
    const winWidth = 600;
    const winHeight = 600;
    const winTop = (screen.height / 2) - (winHeight / 2);
    const winLeft = (screen.width / 2) - (winWidth / 2);
    return `'top=${winTop},left=${winLeft},toolbar=0,status=0,width=${winWidth},height=${winHeight}`;
  }

}

